import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  MessageCircle,
  Clock,
  HelpCircle,
  ShieldCheck,
  FileText,
  Bug,
  ArrowRight,
  Home,
  Search,
} from "lucide-react";
import Navbar from "../components/Navbar";

export default function Support() {
  const [q, setQ] = useState("");

  const faqs = useMemo(
    () => [
      {
        icon: <HelpCircle size={18} />,
        title: "Uygulamayı kullanmak için üyelik gerekli mi?",
        body:
          "Evet. KolayYük’te işlem adımlarının güvenli ilerlemesi için kayıt/oturum açma gereklidir.",
        tags: ["üyelik", "kayıt", "giriş"],
      },
      {
        icon: <MessageCircle size={18} />,
        title: "Sorun bildirimi nasıl yapabilirim?",
        body:
          "Kısa bir açıklama, ekran görüntüsü (varsa) ve mümkünse cihaz/uygulama sürümü ile bize e-posta gönderebilirsin.",
        tags: ["hata", "bug", "support"],
      },
      {
        icon: <Clock size={18} />,
        title: "Geri dönüş süresi nedir?",
        body:
          "Genellikle 24–48 saat içinde dönüş yapıyoruz. Yoğun dönemlerde süre uzayabilir.",
        tags: ["yanıt", "süre", "support"],
      },
      {
        icon: <ShieldCheck size={18} />,
        title: "Verilerim nasıl korunuyor?",
        body:
          "Kişisel veriler KVKK kapsamında işlenir. Ayrıntılar için KVKK ve Gizlilik Politikası sayfalarımızı inceleyebilirsin.",
        tags: ["kvkk", "gizlilik", "privacy"],
      },
      {
        icon: <FileText size={18} />,
        title: "Kullanım koşulları nerede?",
        body:
          "Kullanım Koşulları sayfamızda hizmetin kapsamını ve kuralları bulabilirsin.",
        tags: ["kullanım", "terms"],
      },
    ],
    []
  );

  const filteredFaqs = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return faqs;
    return faqs.filter((x) => {
      const inTitle = x.title.toLowerCase().includes(s);
      const inBody = x.body.toLowerCase().includes(s);
      const inTags = x.tags?.some((t) => t.toLowerCase().includes(s));
      return inTitle || inBody || inTags;
    });
  }, [q, faqs]);

  return (
     <>
          <Navbar />
    <main className="min-h-screen pt-24 sm:pt-28 pb-16 bg-gradient-to-b from-[#f9fafb] via-white to-[#f1f1f1]">
      <div className="max-w-5xl mx-auto px-6">
        {/* Üst başlık */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white text-gray-700">
            <span className="font-semibold text-black">Destek</span>
            <span className="text-gray-400">•</span>
            <span>KolayYük Destek</span>
          </div>

          <h1 className="mt-6 text-4xl md:text-5xl font-extrabold text-black leading-tight">
            Yardım mı lazım?
          </h1>

          <p className="mt-4 text-gray-700 text-base md:text-lg max-w-2xl mx-auto">
            KolayYük ile ilgili destek, geri bildirim veya hata bildirimi için
            bizimle iletişime geçebilirsin. En hızlı çözüm için e-posta ile
            ulaşmanı öneriyoruz.
          </p>

          <p className="mt-3 text-sm text-gray-500">
            Destek e-postası:{" "}
            <a className="underline text-black font-semibold" href="mailto:info@kolayyuk.com">
              info@kolayyuk.com
            </a>
          </p>
        </motion.div>

        {/* CTA kartları */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <a
            href="mailto:info@kolayyuk.com?subject=KolayYük%20Destek%20Talebi&body=Merhaba%2C%0A%0AKonu%3A%20%0AA%C3%A7%C4%B1klama%3A%20%0A%0A%C4%B0%C5%9Fletim%20Sistemi%20(iOS%2FAndroid)%3A%20%0AUygulama%20S%C3%BCr%C3%BCm%C3%BC%3A%20%0ACihaz%20Modeli%3A%20%0A%0AEkran%20g%C3%B6r%C3%BCnt%C3%BCs%C3%BC%20(varsa)%3A%20"
            className="group border-2 border-black rounded-lg p-5 bg-white hover:bg-gray-50 transition"
          >
            <div className="flex items-center gap-3">
              <div className="text-black">
                <Mail size={18} />
              </div>
              <div className="flex-1">
                <div className="text-black font-bold">E-posta ile destek</div>
                <div className="text-gray-600 text-sm mt-1">
                  info@kolayyuk.com
                </div>
              </div>
              <div className="text-black opacity-60 group-hover:opacity-100 transition">
                <ArrowRight size={18} />
              </div>
            </div>
          </a>

          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="flex items-center gap-3">
              <div className="text-black">
                <Clock size={18} />
              </div>
              <div className="flex-1">
                <div className="text-black font-bold">Yanıt süresi</div>
                <div className="text-gray-600 text-sm mt-1">
                  Genellikle 24–48 saat
                </div>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="flex items-center gap-3">
              <div className="text-black">
                <Bug size={18} />
              </div>
              <div className="flex-1">
                <div className="text-black font-bold">Hata bildirimi</div>
                <div className="text-gray-600 text-sm mt-1">
                  Ekran görüntüsü + cihaz bilgisi ekle
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Arama + SSS */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.16 }}
          className="mt-12"
        >
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
                size={18}
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="SSS içinde ara (örn. gizlilik, hata, üyelik)"
                className="w-full pl-11 pr-4 py-3 rounded-md border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4">
              {filteredFaqs.map((x, i) => (
                <div
                  key={i}
                  className="border border-gray-200 rounded-lg p-5 bg-white"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-black mt-0.5">{x.icon}</div>
                    <div className="flex-1">
                      <div className="text-black font-bold">{x.title}</div>
                      <div className="text-gray-700 text-sm mt-2 leading-relaxed">
                        {x.body}
                      </div>

                      {x.tags?.length ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {x.tags.map((t) => (
                            <span
                              key={t}
                              className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}

              {filteredFaqs.length === 0 && (
                <div className="border border-gray-200 rounded-lg p-6 bg-white text-center">
                  <p className="text-gray-700">
                    Bu aramaya uygun bir sonuç bulunamadı.
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    “gizlilik”, “KVKK”, “hata”, “üyelik” gibi kelimeler deneyebilirsin.
                  </p>
                </div>
              )}
            </div>

            {/* Alt linkler */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/kullanim-kosullari"
                className="group border-2 border-black rounded-lg p-5 bg-white hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <FileText size={18} />
                  <div className="flex-1">
                    <div className="text-black font-bold">Kullanım Koşulları</div>
                    <div className="text-gray-600 text-sm mt-1">Hizmet kapsamı ve kurallar</div>
                  </div>
                  <div className="text-black opacity-60 group-hover:opacity-100 transition">→</div>
                </div>
              </Link>

              <Link
                to="/gizlilik"
                className="group border-2 border-black rounded-lg p-5 bg-white hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} />
                  <div className="flex-1">
                    <div className="text-black font-bold">Gizlilik Politikası</div>
                    <div className="text-gray-600 text-sm mt-1">Veri güvenliği ve işleme</div>
                  </div>
                  <div className="text-black opacity-60 group-hover:opacity-100 transition">→</div>
                </div>
              </Link>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-md font-semibold hover:opacity-90 transition"
              >
                <Home size={18} />
                Ana sayfaya dön
              </Link>

              <a
                href="mailto:info@kolayyuk.com?subject=KolayYük%20Destek"
                className="inline-flex items-center justify-center gap-2 border-2 border-black bg-white text-black px-6 py-3 rounded-md font-semibold hover:bg-gray-50 transition"
              >
                <Mail size={18} />
                Destek e-postası yaz
              </a>
            </div>

            {/* Apple için küçük İngilizce blok */}
            <div className="mt-12 text-center text-sm text-gray-500">
              <span className="font-semibold text-black">English:</span> For support, please contact{" "}
              <a className="underline text-black font-semibold" href="mailto:info@kolayyuk.com">
                info@kolayyuk.com
              </a>
              .
            </div>
          </div>
        </motion.div>
      </div>
    </main>
    </>
  );
}